import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, TextField, CircularProgress,
    IconButton, Stack, Button, Dialog, DialogTitle, DialogContent, 
    DialogActions, InputAdornment, Tooltip
} from "@mui/material";
import { 
    Add, Edit, Delete, Search, FileUpload, Visibility 
} from "@mui/icons-material";
import { 
    collection, getDocs, doc, addDoc, updateDoc, deleteDoc, query, orderBy 
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../../firebase"; 
import Swal from "sweetalert2";

function StaffDocuments() {
    const [docs, setDocs] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Form State
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState({ title: "", file: null, existingUrl: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => { fetchDocs(); }, []);

    const fetchDocs = async () => {
        try {
            setLoading(true);
            const q = query(collection(db, "staff_documents"), orderBy("title", "asc"));
            const snap = await getDocs(q);
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setDocs(data);
            setFiltered(data);
        } catch (e) { Swal.fire("Error", "Failed to load", "error"); }
        finally { setLoading(false); }
    };

    const handleSearch = (val) => {
        setSearch(val);
        const filteredData = docs.filter(d => d.title.toLowerCase().includes(val.toLowerCase()));
        setFiltered(filteredData);
        setPage(0);
    };

    const handleOpen = (item = null) => {
        if (item) {
            setEditMode(true);
            setSelectedId(item.id);
            setFormData({ title: item.title, file: null, existingUrl: item.url });
        } else {
            setEditMode(false);
            setFormData({ title: "", file: null, existingUrl: "" });
        }
        setOpen(true);
    };

    const handleSave = async () => {
        if (!formData.title) return Swal.fire("Required", "Please enter title", "warning");
        setSaving(true);
        try {
            const payload = { title: formData.title, updatedAt: new Date() };

            if (editMode) {
                await updateDoc(doc(db, "staff_documents", selectedId), payload);
            } else {
                await addDoc(collection(db, "staff_documents"), { ...payload, createdAt: new Date() });
            }

            Swal.fire("Success", "Document saved", "success");
            setOpen(false);
            fetchDocs();
        } catch (e) { Swal.fire("Error", "Save failed", "error"); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        const res = await Swal.fire({ title: "Delete?", text: "Cannot undo!", icon: "warning", showCancelButton: true });
        if (res.isConfirmed) {
            await deleteDoc(doc(db, "staff_documents", id));
            fetchDocs();
        }
    };

    return (
        <Box p={2}>
            <Stack direction="row" justifyContent="space-between" mb={2} alignItems="center">
                <Typography variant="h6" fontWeight="900" color="primary">Staff Documents</Typography>
                <Stack direction="row" spacing={2}>
                    <TextField 
                        size="small" placeholder="Search title..." value={search} 
                        onChange={(e) => handleSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
                    />
                    <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Add New</Button>
                </Stack>
            </Stack>

            <TableContainer component={Paper} variant="outlined">
                {loading ? <Box p={5} textAlign="center"><CircularProgress /></Box> :
                <Table size="small">
                    <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Sr.</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Document Title</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((d, i) => (
                            <TableRow key={d.id} hover>
                                <TableCell>{(page * rowsPerPage) + i + 1}</TableCell>
                                <TableCell fontWeight="600">{d.title}</TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" color="warning" onClick={() => handleOpen(d)}><Edit fontSize="inherit"/></IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(d.id)}><Delete fontSize="inherit"/></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>}
                <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(e, p) => setPage(p)} />
            </TableContainer>

            {/* Add / Update Popup */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>{editMode ? "Update Document" : "Add New Document"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <TextField fullWidth label="Document Title" size="small" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : "Save Document"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default StaffDocuments;